import {
  Errors,
  defaultInternationalization,
  defaultLanguage,
  fallbackMainHreflang,
  hreflangVariationsFromMain,
} from 'corporate-types';
import { internationalizationToLanguages } from 'corporate-utils';
import { LocalizedPath } from '../models/domain/type';
import { ParsedUrlQuery } from 'querystring';
import { Logger } from 'corporate-utils';

export const fromInternationalizationToHreflang = (
  internationalization: string,
  from?: string
): string | undefined => {
  if (!internationalization) {
    Logger.error(
      Errors.NO_INTERNATIONALIZATION_FOUND,
      internationalization,
      from
    );
    console?.trace?.();
  }

  return fallbackMainHreflang?.[internationalization] || undefined;
};

export const getHreflangVariationsFromMain = (
  mainHreflang: string
): string[] => {
  return hreflangVariationsFromMain?.[mainHreflang] || [];
};

export const canonicalGenerator = (
  locale: string,
  asPath: string,
  query: any
): string => {
  Logger.info('packages/corporate-ui/src/utils/seo.ts params', {
    query,
    asPath,
    locale,
  });

  const queryTranslated: any = {};

  ['page', 'pageSize'].forEach((key: string) => {
    if (query[key]) {
      queryTranslated[key] = query[key];
    }
  });

  let link = '';

  if (Object.keys(queryTranslated).length > 0) {
    const paramKeyValue = Object.keys(queryTranslated)?.map((key: any) => {
      return `${encodeURIComponent(key)}=${encodeURIComponent(
        queryTranslated[key]
      )}`;
    });

    const querySerialized = paramKeyValue.join('&');

    link = `${
      process.env.NEXT_PUBLIC_URL?.endsWith?.('/')
        ? process.env.NEXT_PUBLIC_URL?.slice?.(0, -1)
        : process.env.NEXT_PUBLIC_URL
    }${asPath?.split?.('?')?.[0]}?${querySerialized}`;
  } else {
    link = `${
      process.env.NEXT_PUBLIC_URL?.endsWith?.('/')
        ? process.env.NEXT_PUBLIC_URL.slice?.(0, -1)
        : process.env.NEXT_PUBLIC_URL
    }${asPath?.split?.('?')?.[0]}`;
  }

  if (link === '') {
    // Something went wrong
    link = `${process.env.NEXT_PUBLIC_URL}${
      process.env.NEXT_PUBLIC_URL?.endsWith?.('/') ? '' : '/'
    }${locale}`;
  }

  const linkSanitized = link?.replace?.(/\/$/, '');

  return linkSanitized;
};

export const hrefGenerator = (
  localizedPaths: any[],
  router: any,
  translations: any
) => {
  const alternates: any[] = [];

  localizedPaths
    ?.map?.((localizedPath: LocalizedPath) => {
      if (
        localizedPath &&
        localizedPath.locale &&
        localizedPath.locale !== 'default'
      ) {
        const mainHreflang = fromInternationalizationToHreflang(
          localizedPath?.locale,
          router?.asPath
        );

        const query = JSON.parse(JSON.stringify(router?.query)); // "Deep" copy
        delete query?.slug;

        if (mainHreflang) {
          const variations = getHreflangVariationsFromMain(mainHreflang);

          variations?.map((variation) => {
            const queryTranslated: any = {};

            const language =
              internationalizationToLanguages(
                variation || defaultInternationalization,
                'packages/corporate-ui/src/utils/seo.ts'
              ) || defaultLanguage;

            Object.keys(query).forEach((key: string) => {
              const translationFound = translations.keys.find(
                ({ keysTranslated }: { keysTranslated: string[] }) => {
                  return keysTranslated.indexOf(key) !== -1;
                }
              );

              if (
                translationFound &&
                translationFound.valueTranslated[language]
              ) {
                queryTranslated[translationFound.valueTranslated[language]] =
                  query[key];
              } else {
                if (['page', 'pageSize'].indexOf(key) !== -1) {
                  queryTranslated[key] = query[key];
                }
              }
            });

            let href = `/${mainHreflang}/${encodeURI(
              localizedPath?.href
            )}`?.replace?.(/\/$/, '');

            if (Object.keys(queryTranslated).length > 0) {
              const paramKeyValue = Object.keys(queryTranslated)?.map(
                (key: any) => {
                  return `${encodeURIComponent(key)}=${encodeURIComponent(
                    queryTranslated[key]
                  )}`;
                }
              );

              const querySerialized = paramKeyValue.join('&');

              href = `${href}?${querySerialized}`;
            }

            Logger.info({
              language,
              href: `${process.env.NEXT_PUBLIC_URL}${href}`,
            });

            alternates.push({
              hrefLang: variation,
              href: `${process.env.NEXT_PUBLIC_URL}${href}`,
            });
          });
        }
      }
    })
    ?.filter((noUndefined: any) => !!noUndefined);

  return alternates;
};

export const canonicalPropsGenerator = (
  query: ParsedUrlQuery,
  translations: any,
  locale?: string
) => {
  Logger.info(
    'packages/corporate-ui/src/utils/seo.ts canonicalPropsGenerator',
    query
  );

  let subcategory = '';

  const language =
    internationalizationToLanguages(
      locale || defaultInternationalization,
      'packages/corporate-ui/src/utils/seo.ts canonicalPropsGenerator'
    ) || defaultLanguage;

  Object.keys(query)?.forEach?.((key: string) => {
    const translationFound = translations?.keys?.find?.(
      ({ keysTranslated }: { keysTranslated: string[] }) => {
        return keysTranslated?.indexOf?.(key) !== -1;
      }
    );

    if (translationFound && translationFound?.valueTranslated?.[language]) {
      subcategory = translationFound?.valueTranslated?.[language];
    }
  });

  const url = `${locale ? `/${locale}` : ''}${
    query?.slug && (query?.slug as string[]).length > 0
      ? `/${(query?.slug as string[])?.join?.('/')}`
      : ''
  }${
    subcategory && query?.[subcategory]
      ? `?${subcategory}=${query[subcategory]}`
      : ''
  }`;

  return url;
};
